import { useCallback, useMemo, useState } from 'react';

/** Когда какая ячейка изменена: id строки → ключ колонки гриды → время отметки (мс). */
export type ChangedCells = ReadonlyMap<number, ReadonlyMap<string, number>>;

export interface UseChangedCellsResult {
  changed: ChangedCells;
  /** Сколько строк с отметками. */
  count: number;
  /** Отметить ячейки одной строки. */
  mark: (rowId: number, columnKeys: string[]) => void;
  /** Отметить разом у многих строк (массовая правка) — одна запись в хранилище, а не сотня. */
  markMany: (entries: Array<[number, string[]]>) => void;
  /** Убрать отметки строк — строки удалены. */
  unmarkRows: (rowIds: number[]) => void;
  /** «Снять отметки» — все разом. */
  clear: () => void;
}

type MutableCells = Map<number, Map<string, number>>;

const readStored = (key: string | null): MutableCells => {
  if (!key) return new Map();
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return new Map();
    const parsed = JSON.parse(raw) as Record<string, Record<string, number>>;
    return new Map(
      Object.entries(parsed).map(([rowId, columns]) => [Number(rowId), new Map(Object.entries(columns))]),
    );
  } catch {
    return new Map();
  }
};

const writeStored = (key: string | null, cells: MutableCells) => {
  if (!key) return;
  try {
    if (cells.size === 0) {
      localStorage.removeItem(key);
      return;
    }
    const plain: Record<string, Record<string, number>> = {};
    cells.forEach((columns, rowId) => {
      plain[rowId] = Object.fromEntries(columns);
    });
    localStorage.setItem(key, JSON.stringify(plain));
  } catch {
    // Хранилище недоступно (приватное окно, запрет) — отметки проживут до перезагрузки
  }
};

/**
 * Отметки изменённых ячеек гриды игровых данных — «что я здесь поменял».
 *
 * Правки сохраняются сразу, поэтому отметка — не «несохранённое», а памятка для себя: ячейка
 * залита зелёным, пока человек не нажмёт «Снять отметки». Раньше вместо этого вся строка на пару
 * секунд вспыхивала зелёным и гасла — толку ноль (владелец, 10.09.2026).
 *
 * Хранится в браузере (`localStorage`), переживает перезагрузку и смену фильтров. `storageKey` —
 * свой у таблицы и пользователя; `null` — пользователь ещё не известен, тогда не храним. Отмечаются
 * только свои правки: какие поля поменял другой администратор, бэк не хранит.
 *
 * Строке гриды уходит её собственный набор (`changed.get(id)`): набор меняется только у той строки,
 * которую отметили, поэтому `React.memo` строк не ломается.
 */
export function useChangedCells(storageKey: string | null): UseChangedCellsResult {
  const [state, setState] = useState(() => ({ key: storageKey, cells: readStored(storageKey) }));

  // Ключ сменился (пользователь подгрузился позже) — берём его отметки
  let current = state;
  if (state.key !== storageKey) {
    current = { key: storageKey, cells: readStored(storageKey) };
    setState(current);
  }

  const update = useCallback((change: (prev: MutableCells) => MutableCells) => {
    setState((prev) => {
      const cells = change(prev.cells);
      if (cells === prev.cells) return prev;
      writeStored(prev.key, cells);
      return { key: prev.key, cells };
    });
  }, []);

  const markMany = useCallback(
    (entries: Array<[number, string[]]>) => {
      update((prev) => {
        const now = Date.now();
        const next = new Map(prev);
        let touched = false;
        for (const [rowId, columns] of entries) {
          if (columns.length === 0) continue;
          const row = new Map(next.get(rowId) ?? []);
          columns.forEach((column) => row.set(column, now));
          next.set(rowId, row);
          touched = true;
        }
        return touched ? next : prev;
      });
    },
    [update],
  );

  const mark = useCallback((rowId: number, columnKeys: string[]) => markMany([[rowId, columnKeys]]), [markMany]);

  const unmarkRows = useCallback(
    (rowIds: number[]) => {
      update((prev) => {
        if (!rowIds.some((rowId) => prev.has(rowId))) return prev;
        const next = new Map(prev);
        rowIds.forEach((rowId) => next.delete(rowId));
        return next;
      });
    },
    [update],
  );

  const clear = useCallback(() => update((prev) => (prev.size === 0 ? prev : new Map())), [update]);

  return useMemo(
    () => ({ changed: current.cells, count: current.cells.size, mark, markMany, unmarkRows, clear }),
    [current.cells, mark, markMany, unmarkRows, clear],
  );
}

export default useChangedCells;
